import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useCallback, useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { TechnologyGraphApi } from "../../Api/GraphApi";
import BaseLoader from "../../BaseComponents/BaseLoader";
import { useSelectedCenter } from "../CenterContext";
import { checkStatusCodeSuccess, errHandler, generateColor } from "../../Constant/common";
import { toast } from "react-toastify";
import { InterviewCount, TechnologyGraph } from "../../Constant/SalesDashboard";
import { endDateLabel, startDateLabel } from "../../Constant/Lead";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { Inbox } from "react-feather";
import { useNavigate } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend);

const TechnologyPieChart = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem("sales_tech_start_date") || "";
  });
  const [endDate, setEndDate] = useState(() => {
    return localStorage.getItem("sales_tech_end_date") || "";
  });

  const { selectedCenterPayload } = useSelectedCenter();
  const centerId = selectedCenterPayload?.center_id;

  const handleStartDateChange = (date) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "";
    setStartDate(formattedDate);
    localStorage.setItem("sales_tech_start_date", formattedDate);
  };

  const handleEndDateChange = (date) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "";
    setEndDate(formattedDate);
    localStorage.setItem("sales_tech_end_date", formattedDate);
  };

  const fetchTechnologyGraph = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);

    try {
      const payload = {
        center_id: centerId,
        start_date: startDate,
        end_date: endDate,
      };

      const response = await TechnologyGraphApi(payload);

      if (checkStatusCodeSuccess(response?.statusCode)) {
        const apiData = response?.data?.techCounts || [];

        const labels = apiData.map((item) => item.technology_name);

        const datasets = [
          {
            label: InterviewCount,
            data: apiData.map((item) => item.count),
            backgroundColor: apiData.map((_, index) => generateColor(index, apiData.length)),
            borderWidth: 1,
          },
        ];

        setChartData({ labels, datasets });
      } else {
        setChartData({ labels: [], datasets: [] });
        toast.error(response?.message);
        if (
          typeof response?.message === "string" &&
          response.message.includes("center_id") &&
          response.message.includes("undefined")
        ) {
          navigate("/globalConfiguration");
        }
      }
    } catch (e) {
      setChartData({ labels: [], datasets: [] });
      errHandler(e);
      const errMsg = e?.response?.data?.message || e?.message || "";
      if (
        typeof errMsg === "string" &&
        errMsg.includes("center_id") &&
        errMsg.includes("undefined")
      ) {
        navigate("/globalConfiguration");
      }
    } finally {
      setLoading(false);
    }
  }, [centerId, endDate, navigate, startDate]);

  useEffect(() => {
    fetchTechnologyGraph();
  }, [fetchTechnologyGraph]);
  useEffect(() => {
    const savedStart = localStorage.getItem("sales_tech_start_date");
    const savedEnd = localStorage.getItem("sales_tech_end_date");

    if (!savedStart || !savedEnd) {
      const today = new Date();

      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const formatDate = (date) => moment(date).format("YYYY-MM-DD");

      const defaultStart = formatDate(monthStart);
      const defaultEnd = formatDate(monthEnd);

      setStartDate(defaultStart);
      setEndDate(defaultEnd);
      localStorage.setItem("sales_tech_start_date", defaultStart);
      localStorage.setItem("sales_tech_end_date", defaultEnd);
    }
  }, []);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.parsed || 0;

            const dataArr = context.dataset.data;
            const total = dataArr.reduce((sum, val) => sum + val, 0);

            const percentage = total ? ((value / total) * 100).toFixed(1) : 0;

            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <h6 className="mb-0">{TechnologyGraph}</h6>

        <div className="row">
          <div className="col-lg-12 row">
            <div className="col-lg-6">
              <label className="form-label">{startDateLabel}</label>
              {/* Fixed date handling to avoid timezone offset issues – parsing YYYY-MM-DD as local date */}
              <DatePicker
                selected={startDate ? moment(startDate, "YYYY-MM-DD").toDate() : null}
                onChange={handleStartDateChange}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                maxDate={endDate ? moment(endDate, "YYYY-MM-DD").toDate() : null}
              />
            </div>
            <div className="col-lg-6">
              <label className="form-label">{endDateLabel}</label>
              {/* Fixed date handling to avoid timezone offset issues – parsing YYYY-MM-DD as local date */}
              <DatePicker
                selected={endDate ? moment(endDate, "YYYY-MM-DD").toDate() : null}
                onChange={handleEndDateChange}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                minDate={startDate ? moment(startDate, "YYYY-MM-DD").toDate() : null}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          height: 300,
          display: loading || (chartData && chartData.labels.length === 0) ? "flex" : "block",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading ? (
          <BaseLoader />
        ) : chartData && chartData.labels.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--text-secondary)" }}>
            <Inbox size={48} strokeWidth={1.5} style={{ opacity: 0.5, marginBottom: 12 }} />
            <p style={{ margin: 0, fontSize: "0.9375rem" }}>No records found</p>
          </div>
        ) : (
          chartData && <Pie data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default TechnologyPieChart;
